import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, CalendarDays, MapPin, Image } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import AdminConfirmDialog from '@/components/admin/AdminConfirmDialog';
import toast from 'react-hot-toast';

const AdminArtFairsPage: React.FC = () => {
  const artFairs = useAdminStore((s) => s.artFairs);
  const deleteArtFair = useAdminStore((s) => s.deleteArtFair);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleDelete = (): void => {
    if (!deleteId) return;
    deleteArtFair(deleteId);
    toast.success('Art fair deleted');
    setDeleteId(null);
  };

  const formatDate = (date: string): string => {
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">Art Fairs</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">{artFairs.length} total</p>
        </div>
        <Link to="/admin/art-fairs/new" className="flex items-center gap-2 px-5 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity">
          <Plus size={14} /> New Art Fair
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AnimatePresence mode="popLayout">
          {artFairs.map((fair) => (
            <motion.div key={fair._id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} className="group bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl overflow-hidden hover:border-black/10 dark:hover:border-white/10 transition-all">
              <div className="flex">
                <div className="w-32 h-32 shrink-0 bg-black/[0.02] dark:bg-white/[0.02] overflow-hidden">
                  <img src={fair.poster} alt={fair.title.en} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 p-4 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="text-[13px] font-bold truncate">{fair.title.en}</h4>
                    <div className="flex items-center gap-1 shrink-0">
                      <Link to={`/admin/art-fairs/${fair._id}`} className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 dark:text-white/20 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.04] transition-all">
                        <Pencil size={12} />
                      </Link>
                      <button onClick={() => setDeleteId(fair._id)} className="w-7 h-7 rounded-lg flex items-center justify-center text-black/20 dark:text-white/20 hover:text-red-500 hover:bg-red-500/5 transition-all">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-center gap-1.5 text-[10px] text-black/40 dark:text-white/40">
                      <CalendarDays size={10} />
                      <span>{formatDate(fair.startDate)} — {formatDate(fair.endDate)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-black/40 dark:text-white/40">
                      <MapPin size={10} />
                      <span className="truncate">{fair.location.en}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-black/40 dark:text-white/40">
                      <Image size={10} />
                      <span>{fair.artworks.length} artwork{fair.artworks.length !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {artFairs.length === 0 && (
        <div className="text-center py-16">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">No art fairs found</p>
        </div>
      )}

      <AdminConfirmDialog isOpen={deleteId !== null} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Delete Art Fair" message="This will permanently remove this art fair. This action cannot be undone." />
    </div>
  );
};

export default AdminArtFairsPage;
